import { Component, OnInit } from '@angular/core';
import moment, { Moment } from 'moment';
import * as _ from 'lodash';
import { HoursForDay } from '@src/app/common/models/hours-for-day';
import { IPrepLocation, PrepLocationForComponent } from 'src/app/common/models/prep-location';
import { IPrepLocationHoursOverride } from 'src/app/common/models/prep-location-hours-override';
import { IMultiStoreSelect } from 'src/app/common/models/multiStoreSelect';
import { Operation } from 'src/app/common/models/operation';
import { PrepLocationsService } from 'src/app/services/prep-locations.service';
import { ToastrService } from 'ngx-toastr';
import { AuthManager } from 'src/app/core/auth/auth.manager';
import { MultiStoreComponent } from 'src/app/_layouts/components/modal/multi-store/multi-store.component';
import { MatDialog } from '@angular/material/dialog';
import { AdminUIHelpers } from 'src/app/common/helpers';
import { HoursExceptionScheduleComponent } from '../../components/modal/hours-exception-schedule/hours-exception-schedule.component';
import { HoursExceptionViewComponent } from '../../components/modal/hours-exception-view/hours-exception-view.component';
import { catchError, combineLatestWith, forkJoin, map, Observable, of } from 'rxjs';
import { FacilityManager } from 'src/app/managers/facility.manager';
import { RemoveAllHoursExceptionsComponent } from '../../components/modal/remove-all-hours-exceptions/remove-all-hours-exceptions.component';
import { ObservableResponses } from 'src/app/common/models/ObservableResponses';
import { MultiPrepLocationComponent } from '../../components/modal/multi-prep-location/multi-prep-location.component';
import { PatchPrepLocationsRequest } from 'src/app/common/models/patchPrepLocationsRequest';
import { MultiPrepSelect } from 'src/app/common/models/multiPrepSelect';

import { MatButton } from '@angular/material/button';
import {
  MatAccordion,
  MatExpansionPanel,
  MatExpansionPanelHeader,
  MatExpansionPanelTitle,
  MatExpansionPanelDescription,
  MatExpansionPanelContent,
} from '@angular/material/expansion';
import { MatGridList, MatGridTile } from '@angular/material/grid-list';
import { MatFormField, MatLabel } from '@angular/material/input';
import { MatSelect, MatOption } from '@angular/material/select';
import { IncrementInputComponent } from '../../../modules/shared/components/increment-input/increment-input.component';
import { DayPipe, TimeOnlyPipe } from '@pipes/time-date-pipe';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { FormsModule } from '@angular/forms';
import { PatchPrepLocationsItem } from '@src/app/common/models/patchPrepLocationsItem';

interface HoursUpdate {
  index: number;
  fieldName: 'openTime' | 'closeTime';
  dateValue: moment.Moment;
}

@Component({
  selector: 'app-prep-locations',
  templateUrl: './prep-locations.component.html',
  styleUrls: ['./prep-locations.component.scss'],
  standalone: true,
  imports: [
    FormsModule,
    MatButton,
    MatAccordion,
    MatExpansionPanel,
    MatExpansionPanelHeader,
    MatExpansionPanelTitle,
    MatExpansionPanelDescription,
    MatExpansionPanelContent,
    MatGridList,
    MatGridTile,
    MatFormField,
    MatLabel,
    MatSelect,
    MatSlideToggle,
    MatOption,
    IncrementInputComponent,
    DayPipe,
    TimeOnlyPipe,
  ],
})
export class PrepLocationsComponent implements OnInit {
  public authManager: AuthManager;
  public currentFacilityId: string | any;
  public facilityPrepLocations: IPrepLocation[] = [];
  public facilityHoursOverrides: IPrepLocationHoursOverride[] = [];
  public prepLocationForComponents: PrepLocationForComponent[] = [];
  public timeRange: any[] = AdminUIHelpers.getTimeRange();

  public sections: Record<string, any> = {
    capacity: {
      name: 'capacity',
      changedState: 0,
      storesSelected: 1,
      multiStoreFacilitySelection: [],
    },
    defaultPrepMinutes: {
      name: 'defaultPrepMinutes',
      changedState: 0,
      storesSelected: 1,
      multiStoreFacilitySelection: [],
    },
    hours: {
      name: 'hours',
      changedState: [],
      storesSelected: 1,
      multiStoreFacilitySelection: [],
      prepLocationsSelected: 1,
      multiPrepLocationSelection: [],
    },
    earlyPrepWarning: {
      name: 'earlyPrepWarning',
      changedState: false,
      storesSelected: 1,
      multiStoreFacilitySelection: [],
    },
    allDayView: {
      name: 'allDayView',
      changedState: false,
      storesSelected: 1,
      multiStoreFacilitySelection: [],
    },
  };

  public readonly EARLY_PREP_DISPLAY_TRUE = 'Display the Early Prep Warning';
  public readonly EARLY_PREP_DISPLAY_FALSE = 'Do not display the Early Prep Warning';
  public readonly ALL_DAY_VIEW_DISPLAY_TRUE = 'Display the All Day View';
  public readonly ALL_DAY_VIEW_DISPLAY_FALSE = 'Do not display the All Day View';

  constructor(
    private prepLocationsService: PrepLocationsService,
    private _facilityManager: FacilityManager,
    private toastr: ToastrService,
    private auth: AuthManager,
    private dialog: MatDialog,
  ) {
    this.authManager = this.auth;
  }

  public ngOnInit(): void {
    this.auth.isLoggedIn$.subscribe((isAuthed) => {
      if (isAuthed) {
        this._facilityManager.facilityIdUpdates$.subscribe((fId: any) => {
          this.currentFacilityId = fId;
          this.getFacilityPrepLocations();
        });
      }
    });
  }

  public getFacilityPrepLocations(): void {
    const prepLocations$ = this.prepLocationsService.getPrepLocations(this.currentFacilityId);
    const hoursOverrides$ = this.prepLocationsService.getPrepLocationHoursOverrides(this.currentFacilityId);

    prepLocations$.pipe(combineLatestWith(hoursOverrides$)).subscribe({
      next: ([prepLocs, overrides]) => {
        this.facilityPrepLocations = prepLocs;
        this.facilityHoursOverrides = overrides;
        this.MapIPrepLocationsToPrepLocationForComponents();
      },
      error: (e) => {
        this.toastr.error('Could not load Prep Locations');
      },
    });
  }

  public MapIPrepLocationsToPrepLocationForComponents(): void {
    this.prepLocationForComponents = [];
    this.facilityPrepLocations.forEach((p) => {
      // use prep locations from the API to map to larger objects for the component

      const prepLocationForComponent: PrepLocationForComponent = {
        organizationId: p.organizationId,
        facilityId: p.facilityId,
        prepLocationTypeName: p.prepLocationTypeName,
        walkPathOrder: p.walkPathOrder,
        isAvailable: p.isAvailable,
        openTime: p.openTime,
        closeTime: p.closeTime,
        defaultPrepMinutes: p.defaultPrepMinutes,
        maxSlotItemCount: p.maxSlotItemCount,
        alertOnEarlyStartPrep: p.alertOnEarlyStartPrep,
        showAllDayView: p.showAllDayView,
        // leave hoursForDays empty for now, to be created below
        hoursForDays: [],
        hoursOverrides: this.facilityHoursOverrides.filter(
          (h) =>
            h.organizationId === p.organizationId
            && h.facilityId === p.facilityId
            && h.prepLocationTypeName === p.prepLocationTypeName,
        ),
        openTimeToday: this.getOpenTimeToday(p),
        closeTimeToday: this.getCloseTimeToday(p),
      };

      // add on the open and close index (index in the timeRange list) for each day of the week
      p.hoursForDays.forEach((day) => {
        day.openIndex = this.timeRange.findIndex((t) => t.time.isSame(day.openTime));
        day.closeIndex = this.timeRange.findIndex((t) => t.time.isSame(day.closeTime));
        prepLocationForComponent.hoursForDays.push(day);
      });

      this.prepLocationForComponents.push(prepLocationForComponent);
    });
  }
  private getOpenTimeToday(p: IPrepLocation): moment.Moment {
    const hoursOverrides = this.facilityHoursOverrides.filter(
      (h) =>
        h.organizationId === p.organizationId
        && h.facilityId === p.facilityId
        && h.prepLocationTypeName === p.prepLocationTypeName
        && moment(h.date).isSame(moment.utc(), 'day'),
    );

    if (hoursOverrides.length > 0 && hoursOverrides[0].openTime) {
      return AdminUIHelpers.convertHoursOverridesTimeFieldToMoment(hoursOverrides[0].openTime);
    }
    return p.hoursForDays[moment.utc().day()].openTime;
  }

  private getCloseTimeToday(p: IPrepLocation): moment.Moment {
    const hoursOverrides = this.facilityHoursOverrides.filter(
      (h) =>
        h.organizationId === p.organizationId
        && h.facilityId === p.facilityId
        && h.prepLocationTypeName === p.prepLocationTypeName
        && moment(h.date).isSame(moment.utc(), 'day'),
    );

    if (hoursOverrides.length > 0 && hoursOverrides[0].closeTime) {
      return AdminUIHelpers.convertHoursOverridesTimeFieldToMoment(hoursOverrides[0].closeTime);
    }
    return p.hoursForDays[moment.utc().day()].closeTime;
  }

  // when opening an expansion panel, set up the component's change tracking
  public setHoursChanged(prepLocationTypeName: string): void {
    const prepLocation = this.prepLocationForComponents.find((b) => b.prepLocationTypeName === prepLocationTypeName);
    if (prepLocation) {
      this.sections.hours.changedState = _.cloneDeep(prepLocation.hoursForDays);
      this.sections.capacity.changedState = prepLocation.maxSlotItemCount;
      this.sections.defaultPrepMinutes.changedState = prepLocation.defaultPrepMinutes;
      this.sections.earlyPrepWarning.changedState = prepLocation.alertOnEarlyStartPrep;
      this.sections.allDayView.changedState = prepLocation.showAllDayView;
    }
  }

  private setHoursAfterSave(pl: IPrepLocation, hoursChangeList: HoursUpdate[]): void {
    const prepLocation = this.prepLocationForComponents.find((b) => b.prepLocationTypeName === pl.prepLocationTypeName);
    if (prepLocation) {
      hoursChangeList.forEach((change) => {
        prepLocation.hoursForDays[change.index][change.fieldName] = change.dateValue;
      });
      const affectedIndices = new Set(hoursChangeList.map((c) => c.index));
      affectedIndices.forEach((i) => {
        const day = prepLocation.hoursForDays[i];
        day.openIndex = this.timeRange.findIndex((t) => t.time.isSame(day.openTime));
        day.closeIndex = this.timeRange.findIndex((t) => t.time.isSame(day.closeTime));
      });
      prepLocation.maxSlotItemCount = this.sections.capacity.changedState;
      prepLocation.defaultPrepMinutes = this.sections.defaultPrepMinutes.changedState;
      prepLocation.alertOnEarlyStartPrep = this.sections.earlyPrepWarning.changedState;
      prepLocation.openTimeToday = this.getOpenTimeToday(pl);
      prepLocation.closeTimeToday = this.getCloseTimeToday(pl);
    }
  }

  public saveCapacity(prepLocation: PrepLocationForComponent): void {
    const patch: Operation[] = [
      {
        op: 'add',
        path: '/maxSlotItemCount',
        value: this.sections.capacity.changedState,
      },
    ];

    const saveObservables = this.buildObservableCollection(prepLocation, this.sections.capacity.name, patch);

    forkJoin(saveObservables).subscribe({
      next: (f) => {
        const success = this.prepToastrMessages(f, prepLocation);
        if (success) {
          prepLocation.maxSlotItemCount = this.sections.capacity.changedState;
          this.resetMultiStoreSelections(this.sections.capacity.name);
          this.refreshStoresSelected(this.sections.capacity.name);
        }
      },
      error: (e) => {
        this.toastr.error(
          'An error occurred while updating Prep Location [' + prepLocation.prepLocationTypeName + ']: ' + e.error,
        );
        return;
      },
    });
  }

  public cancelCapacity(prepLocation: PrepLocationForComponent): void {
    this.sections.capacity.changedState = prepLocation.maxSlotItemCount;
    this.resetMultiStoreSelections(this.sections.capacity.name);
    this.refreshStoresSelected(this.sections.capacity.name);
  }

  public saveDefaultPrepMinutes(prepLocation: PrepLocationForComponent): void {
    const patch: Operation[] = [
      {
        op: 'add',
        path: '/defaultPrepMinutes',
        value: this.sections.defaultPrepMinutes.changedState,
      },
    ];

    const saveObservables = this.buildObservableCollection(prepLocation, this.sections.defaultPrepMinutes.name, patch);

    forkJoin(saveObservables).subscribe({
      next: (f) => {
        const success = this.prepToastrMessages(f, prepLocation);
        if (success) {
          prepLocation.defaultPrepMinutes = this.sections.defaultPrepMinutes.changedState;
          this.resetMultiStoreSelections(this.sections.defaultPrepMinutes.name);
          this.refreshStoresSelected(this.sections.defaultPrepMinutes.name);
        }
      },
      error: (e) => {
        this.toastr.error(
          'An error occurred while updating Prep Location [' + prepLocation.prepLocationTypeName + ']: ' + e.error,
        );
        return;
      },
    });
  }

  public saveEarlyPrepWarning(prepLocation: PrepLocationForComponent): void {
    const patch: Operation[] = [
      {
        op: 'add',
        path: '/alertOnEarlyStartPrep',
        value: this.sections.earlyPrepWarning.changedState,
      },
    ];

    const saveObservables = this.buildObservableCollection(prepLocation, this.sections.earlyPrepWarning.name, patch);

    forkJoin(saveObservables).subscribe({
      next: (f) => {
        const success = this.prepToastrMessages(f, prepLocation);
        if (success) {
          prepLocation.alertOnEarlyStartPrep = this.sections.earlyPrepWarning.changedState;
          this.resetMultiStoreSelections(this.sections.earlyPrepWarning.name);
          this.refreshStoresSelected(this.sections.earlyPrepWarning.name);
        }
      },
      error: (e) => {
        this.toastr.error(
          'An error occurred while updating Prep Location [' + prepLocation.prepLocationTypeName + ']: ' + e.error,
        );
        return;
      },
    });
  }

  public cancelEarlyPrepWarning(prepLocation: PrepLocationForComponent): void {
    this.sections.earlyPrepWarning.changedState = prepLocation.alertOnEarlyStartPrep;
    this.resetMultiStoreSelections(this.sections.earlyPrepWarning.name);
    this.refreshStoresSelected(this.sections.earlyPrepWarning.name);
  }

  public saveAllDayView(prepLocation: PrepLocationForComponent): void {
    const patch: Operation[] = [
      {
        op: 'add',
        path: '/showAllDayView',
        value: this.sections.allDayView.changedState,
      },
    ];

    const saveObservables = this.buildObservableCollection(prepLocation, this.sections.allDayView.name, patch);

    forkJoin(saveObservables).subscribe({
      next: (f) => {
        const success = this.prepToastrMessages(f, prepLocation);
        if (success) {
          prepLocation.showAllDayView = this.sections.allDayView.changedState;
          this.resetMultiStoreSelections(this.sections.allDayView.name);
          this.refreshStoresSelected(this.sections.allDayView.name);
        }
      },
      error: (e) => {
        this.toastr.error(
          'An error occurred while updating Prep Location [' + prepLocation.prepLocationTypeName + ']: ' + e.error,
        );
        return;
      },
    });
  }

  public cancelAllDayView(prepLocation: PrepLocationForComponent): void {
    this.sections.allDayView.changedState = prepLocation.showAllDayView;
    this.resetMultiStoreSelections(this.sections.allDayView.name);
    this.refreshStoresSelected(this.sections.allDayView.name);
  }

  public cancelDefaultPrep(prepLocation: PrepLocationForComponent): void {
    this.sections.defaultPrepMinutes.changedState = prepLocation.defaultPrepMinutes;
    this.resetMultiStoreSelections(this.sections.defaultPrepMinutes.name);
    this.refreshStoresSelected(this.sections.defaultPrepMinutes.name);
  }

  public openTimeSelected(selectedMoment: moment.Moment, dayOfWeek: number, prepLocationTypeName: string): void {
    this.sections.hours.changedState[dayOfWeek].openTime = selectedMoment;
  }

  public closeTimeSelected(selectedMoment: moment.Moment, dayOfWeek: number, prepLocationTypeName: string): void {
    this.sections.hours.changedState[dayOfWeek].closeTime = selectedMoment;
  }

  public hoursChanged(prepLocation: PrepLocationForComponent): boolean {
    return _.isEqual(this.sections.hours.changedState, prepLocation.hoursForDays);
  }

  public cancelHours(prepLocation: PrepLocationForComponent): void {
    this.sections.hours.changedState = prepLocation.hoursForDays;
    this.setHoursChanged(prepLocation.prepLocationTypeName);
    this.resetMultiStoreSelections(this.sections.hours.name);
    this.refreshStoresSelected(this.sections.hours.name);
  }

  private createHoursPatchOperations(prepLocationName: string, hoursChanged: HoursUpdate[]): PatchPrepLocationsItem {
    const pplItem: PatchPrepLocationsItem = {
      prepLocationName: prepLocationName,
      patch: [],
    };

    hoursChanged.forEach((change) => {
      pplItem.patch.push(this.createHoursForDayPatch(change.index, change.dateValue, change.fieldName));
    });
    return pplItem;
  }

  private buildHoursChangeList(prepLocation: PrepLocationForComponent): HoursUpdate[] {
    const changeList: HoursUpdate[] = [];
    this.sections.hours.changedState.forEach((changedDay: HoursForDay, index: number) => {
      if (changedDay.openTime.isSame(prepLocation.hoursForDays[index].openTime, 'm') === false) {
        changeList.push({
          dateValue: changedDay.openTime,
          fieldName: 'openTime',
          index: index,
        });
      }
      if (changedDay.closeTime.isSame(prepLocation.hoursForDays[index].closeTime, 'm') === false) {
        changeList.push({
          dateValue: changedDay.closeTime,
          fieldName: 'closeTime',
          index: index,
        });
      }
    });
    return changeList;
  }

  private createHoursForDayPatch(pathIndex: number, newValue: moment.Moment, field: string): Operation {
    return {
      op: 'replace',
      path: `/hoursForDays/${pathIndex}/${field}`,
      value: newValue.format('HH:mm:ss'),
    };
  }

  public saveHours(prepLocation: PrepLocationForComponent): void {
    const hoursChangeList = this.buildHoursChangeList(prepLocation);

    const patchPrepLocationsRequest: PatchPrepLocationsRequest = {
      patchPrepLocationsItems: [],
    };

    // automatically select the current prep location if none were selected via the modal
    if (this.sections.hours.multiPrepLocationSelection.length < 1) {
      this.sections.hours.multiPrepLocationSelection.push({
        prepLocationName: prepLocation.prepLocationTypeName,
        isSelected: true,
      });
    }

    // get the selected prep locations so we can build the patch payload for a store
    const selectedPrepLocations = this.sections.hours.multiPrepLocationSelection.filter((f: MultiPrepSelect) => {
      return f.isSelected;
    });
    for (const updatedPrepLocation of selectedPrepLocations) {
      patchPrepLocationsRequest.patchPrepLocationsItems.push(
        this.createHoursPatchOperations(updatedPrepLocation.prepLocationName, hoursChangeList),
      );
    }

    // build the requests for each store containing the update to each selected prep location
    const saveObservables = this.buildObservableCollectionBulk(
      prepLocation,
      this.sections.hours.name,
      patchPrepLocationsRequest,
    );

    forkJoin(saveObservables).subscribe({
      next: (r) => {
        const success = this.prepToastrMessages(r, prepLocation);
        if (success) {
          // refresh all the selected prep locations
          selectedPrepLocations.forEach((spl: MultiPrepSelect) => {
            const updatedPrepLocation = this.prepLocationForComponents.find(
              (pl) => pl.prepLocationTypeName === spl.prepLocationName,
            );
            if (updatedPrepLocation) {
              this.setHoursAfterSave(updatedPrepLocation, hoursChangeList);
            }
          });
          this.resetMultiStoreSelections(this.sections.hours.name);
          this.refreshStoresSelected(this.sections.hours.name);
          this.resetSelectedPrepLocations(this.sections.hours.name);
        }
      },
      error: (e) => {
        this.toastr.error(
          'An error occurred while updating Prep Location [' + prepLocation.prepLocationTypeName + '] for Facility ',
        );
      },
    });
  }

  private resetMultiStoreSelections(section: string): void {
    for (const s of this.sections[section].multiStoreFacilitySelection) {
      s.isSelected = s.facilityId === this.currentFacilityId;
    }
    this.sections[section].multiStoreFacilitySelection.sort((a: any, b: any) =>
      +a.facilityId > +b.facilityId ? 1 : -1,
    );
  }

  public refreshStoresSelected(section: string): void {
    this.sections[section].storesSelected = this.sections[section].multiStoreFacilitySelection.filter((f: any) => {
      return f.isSelected === true;
    }).length;
  }

  private resetSelectedPrepLocations(section: string): void {
    this.sections[section].multiPrepLocationSelection.length = 0;
  }

  private refreshPrepLocationsSelected(section: string): void {
    this.sections[section].prepLocationsSelected = this.sections[section].multiPrepLocationSelection.filter(
      (f: any) => {
        return f.isSelected === true;
      },
    ).length;
  }

  private openMultiStoreDialog(section: string, prepLocation?: PrepLocationForComponent): void {
    const edits = prepLocation ? this.buildEditsDescription(prepLocation) : [];
    const dialogRef = this.dialog.open(MultiStoreComponent, {
      width: '600px',
      maxHeight: '700px',
      maxWidth: '700px',
      data: {
        multiStoreData: this.sections[section].multiStoreFacilitySelection,
        edits: edits,
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.sections[section].multiStoreFacilitySelection = result;
        this.refreshStoresSelected(section);
      }
    });
  }

  private openMultiPrepLocationDialog(
    prepLocationName: string,
    section: string,
    prepLocation?: PrepLocationForComponent,
  ): void {
    const edits = prepLocation ? this.buildEditsDescription(prepLocation) : [];
    const dialogRef = this.dialog.open(MultiPrepLocationComponent, {
      width: '600px',
      maxHeight: '700px',
      maxWidth: '700px',
      data: {
        multiPrepLocationData: this.sections[section].multiPrepLocationSelection,
        prepLocationName: prepLocationName,
        edits: edits,
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.sections[section].multiPrepLocationSelection = result;
        this.refreshPrepLocationsSelected(section);
      }
    });
  }

  private buildEditsDescription(prepLocation: PrepLocationForComponent): string[] {
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const edits: string[] = [];

    this.sections.hours.changedState.forEach((changedDay: HoursForDay, index: number) => {
      const originalDay = prepLocation.hoursForDays[index];
      if (!changedDay.openTime.isSame(originalDay.openTime, 'm')) {
        edits.push(`Business Hours, ${days[index]}, Open: ${changedDay.openTime.format('h:mm A')}`);
      }
      if (!changedDay.closeTime.isSame(originalDay.closeTime, 'm')) {
        edits.push(`Business Hours, ${days[index]}, Close: ${changedDay.closeTime.format('h:mm A')}`);
      }
    });
    return edits;
  }

  private openHoursExceptionDialog(prepLocation: PrepLocationForComponent): void {
    const dialogRef = this.dialog.open(HoursExceptionScheduleComponent, {
      width: '600px',
      maxHeight: '750px',
      maxWidth: '800px',
      data: {
        prepLocation: prepLocation,
      },
    });

    dialogRef.afterClosed().subscribe((result: IPrepLocationHoursOverride[]) => {
      if (result.length > 0) {
        result.forEach((o) => {
          this.applyHoursOverrideToPrepLocation(o);
        });
      }
    });
  }

  public openRemoveScheduledHoursOverridesDialog(): void {
    const dialogRef = this.dialog.open(RemoveAllHoursExceptionsComponent, {
      width: '600px',
      maxHeight: '750px',
      maxWidth: '800px',
    });

    dialogRef.afterClosed().subscribe((result: Moment) => {
      if (result) {
        const dateCompareFormat = 'MM/DD/YYYY';
        const overridesForDay = this.facilityHoursOverrides.filter((fho) => {
          const dateString = moment(fho.date).format(dateCompareFormat);
          return dateString === result.format(dateCompareFormat);
        });
        const saveObservables: Observable<any>[] = [];
        if (overridesForDay.length === 0) {
          this.toastr.info(`No overrides for date: ${result.format('dddd, MMMM Do, YYYY')}`);
        }
        overridesForDay.forEach((override) => {
          saveObservables.push(
            this.prepLocationsService.deleteHoursOverride(override).pipe(
              map((success) => ({ isError: false, error: null, override: override.prepLocationTypeName })),
              catchError((err) => of({ isError: true, error: err, override: override.prepLocationTypeName })),
            ),
          );
        });
        forkJoin(saveObservables).subscribe({
          next: (r) => {
            const success = this.removeHandoffExceptionsToastrMessages(r, result);
            if (success) {
              this._removeCachedHoursOverridesForDate(r, result);
            }
          },
          error: (e) => {
            this.toastr.error('An error occurred while removing Hours Overrides');
          },
        });
      }
    });
  }

  private openHoursExceptionViewDialog(prepLocation: PrepLocationForComponent): void {
    const dialogRef = this.dialog.open(HoursExceptionViewComponent, {
      width: '500px',
      maxHeight: '600px',
      maxWidth: '700px',
      data: prepLocation,
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
      }
    });
  }

  private applyHoursOverrideToPrepLocation(override: IPrepLocationHoursOverride): void {
    const prepLocationIndex = this.prepLocationForComponents.findIndex(
      (pl) => pl.prepLocationTypeName === override.prepLocationTypeName,
    );

    const baseDay = this.prepLocationForComponents[prepLocationIndex].hoursForDays[new Date(override.date).getDay()];
    if (!override.openTime && baseDay) {
      override.openTime = baseDay.openTime.format('HH:mm:ss');
    }
    if (!override.closeTime && baseDay) {
      override.closeTime = baseDay.closeTime.format('HH:mm:ss');
    }

    const existingHoursOverrides = this.prepLocationForComponents[prepLocationIndex].hoursOverrides;

    const facilityOverrides = this.facilityHoursOverrides.filter((fho) => {
      return !(fho.date === override.date) && fho.prepLocationTypeName === override.prepLocationTypeName;
    });
    facilityOverrides.push(override);
    this.facilityHoursOverrides = facilityOverrides;

    // if there are no overrides for prep location, add and bail
    if (existingHoursOverrides.length < 1) {
      this.prepLocationForComponents[prepLocationIndex].hoursOverrides.push(override);
      return;
    }

    // else if check if we need to upsert the override or push and sort;
    const existingIndex = existingHoursOverrides.findIndex((o) => {
      return new Date(o.date).getTime() === new Date(override.date).getTime();
    });

    if (existingIndex > -1) {
      this.prepLocationForComponents[prepLocationIndex].hoursOverrides[existingIndex] = override;
    } else {
      existingHoursOverrides.push(override);
      this.prepLocationForComponents[prepLocationIndex].hoursOverrides = existingHoursOverrides.sort(
        (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime(),
      );
    }
  }

  private _removeCachedHoursOverridesForDate(results: any[], date: Moment): void {
    const responseResults = this.splitObservableResults(results);
    responseResults.successfulObservables.forEach((success) => {
      const prepType = success.override;
      this.facilityHoursOverrides = this.facilityHoursOverrides.filter((fho) => {
        !fho.prepLocationTypeName === prepType && !moment(fho.date).isSame(date);
      });
      const prepIndex = this.prepLocationForComponents.findIndex((pl) => pl.prepLocationTypeName === prepType);
      this.prepLocationForComponents[prepIndex].hoursOverrides = this.prepLocationForComponents[
        prepIndex
      ].hoursOverrides.filter((override) => !moment(override.date).isSame(date));
    });
  }

  public applyHoursToMultiplePrepLocations(prepLocation: PrepLocationForComponent): void {
    this.openMultiPrepLocationDialog(prepLocation.prepLocationTypeName, this.sections.hours.name, prepLocation);
  }

  public applyHoursToMultipleStores(prepLocation: PrepLocationForComponent): void {
    this.openMultiStoreDialog(this.sections.hours.name, prepLocation);
  }

  public applyCapacityToMultipleStores(prepLocation: PrepLocationForComponent): void {
    this.openMultiStoreDialog(this.sections.capacity.name);
  }

  public applyDefaultPrepToMultipleStores(prepLocation: PrepLocationForComponent): void {
    this.openMultiStoreDialog(this.sections.defaultPrepMinutes.name);
  }

  public applyEarlyPrepWarningToMultipleStores(prepLocation: PrepLocationForComponent): void {
    this.openMultiStoreDialog(this.sections.earlyPrepWarning.name);
  }

  public applyAllDayViewToMultipleStores(prepLocation: PrepLocationForComponent): void {
    this.openMultiStoreDialog(this.sections.allDayView.name);
  }

  public scheduleHoursException(prepLocation: PrepLocationForComponent): void {
    this.openHoursExceptionDialog(prepLocation);
  }

  public viewScheduledHoursExceptions(prepLocation: PrepLocationForComponent): void {
    this.openHoursExceptionViewDialog(prepLocation);
  }

  private buildObservableCollection(
    prepLocation: PrepLocationForComponent,
    section: string,
    patch: Operation[],
  ): Observable<any>[] {
    const saveObservables: Observable<any>[] = [];
    const facilitiesSelected = this.sections[section].multiStoreFacilitySelection.filter((f: IMultiStoreSelect) => {
      return f.isSelected;
    });

    if (facilitiesSelected.length === 0) {
      const currentFacility: IMultiStoreSelect = {
        facilityId: prepLocation.facilityId,
        facilityName: '',
        isSelected: true,
      };
      facilitiesSelected.push(currentFacility);
    }

    facilitiesSelected.forEach((f: IMultiStoreSelect) => {
      saveObservables.push(
        this.prepLocationsService.updatePrepLocation(f.facilityId, prepLocation.prepLocationTypeName, patch).pipe(
          map((success) => ({ isError: false, error: null, facilityId: f.facilityId })),
          catchError((err) => of({ isError: true, error: err, facilityId: f.facilityId })),
        ),
      );
    });
    return saveObservables;
  }

  private splitObservableResults(observableResponses: any[]): ObservableResponses {
    return {
      successfulObservables: observableResponses.filter((e) => e.isError === false),
      erroredObservables: observableResponses.filter((e) => e.isError === true),
    };
  }

  private buildObservableCollectionBulk(
    prepLocation: PrepLocationForComponent,
    section: string,
    resource: PatchPrepLocationsRequest,
  ): Observable<any>[] {
    const saveObservables: Observable<any>[] = [];
    const facilitiesSelected = this.sections[section].multiStoreFacilitySelection.filter((f: IMultiStoreSelect) => {
      return f.isSelected;
    });

    if (facilitiesSelected.length === 0) {
      const currentFacility: IMultiStoreSelect = {
        facilityId: prepLocation.facilityId,
        facilityName: '',
        isSelected: true,
      };
      facilitiesSelected.push(currentFacility);
    }

    facilitiesSelected.forEach((f: IMultiStoreSelect) => {
      saveObservables.push(
        this.prepLocationsService.updatePrepLocations(f.facilityId, resource).pipe(
          map((success) => ({ isError: false, error: null, facilityId: f.facilityId })),
          catchError((err) => of({ isError: true, error: err, facilityId: f.facilityId })),
        ),
      );
    });
    return saveObservables;
  }

  private prepToastrMessages(observableResults: any[], prepLocation: PrepLocationForComponent): boolean {
    const responses = this.splitObservableResults(observableResults);
    if (responses.erroredObservables.length === 0) {
      this.toastr.success('Prep Location ' + prepLocation.prepLocationTypeName + ' updated!');
    }
    if (responses.erroredObservables.length > 0 && responses.successfulObservables.length > 0) {
      this.toastr.warning(
        responses.successfulObservables.length
          + ' facility prep locations were updated, however '
          + responses.erroredObservables.length
          + ' failed to update: '
          + responses.erroredObservables.map((eo) => eo.facilityId).join(', '),
        'Not all Facility Prep Locations updated',
        { timeOut: 10000 },
      );
    }
    if (responses.successfulObservables.length === 0) {
      this.toastr.error('An error occurred while updating Prep Location [' + prepLocation.prepLocationTypeName + ']');
      return false;
    }
    return true;
  }

  private removeHandoffExceptionsToastrMessages(observableResults: any[], date: Moment): boolean {
    const responses = this.splitObservableResults(observableResults);
    const dateFormatString = 'dddd, MMMM Do YYYY';
    if (responses.erroredObservables.length === 0 && responses.successfulObservables.length === 0) {
      this.toastr.error(`Unexpected error: no request attempts for date: ${date.format(dateFormatString)}`);
      return false;
    }
    if (responses.successfulObservables.length === 0 && responses.erroredObservables.length > 0) {
      this.toastr.error(`An error occurred while removing Hours Overrides for ${date.format(dateFormatString)}`);
      return false;
    }
    if (responses.erroredObservables.length === 0) {
      this.toastr.success(`Hours Overrides removed for: ${date.format(dateFormatString)}`);
    }
    if (responses.erroredObservables.length > 0 && responses.successfulObservables.length > 0) {
      this.toastr.warning(
        responses.successfulObservables.length
          + ' Hours Overrides were removed, however '
          + responses.erroredObservables.length
          + ' failed to update: '
          + responses.erroredObservables.map((eo) => eo.prepLocationTypeName).join(', '),
        'Not all Hours Overrides removed',
        { timeOut: 10000 },
      );
    }
    return true;
  }

  public earlyPrepWarningText(earlyPrepChangedState: boolean): string {
    return earlyPrepChangedState ? this.EARLY_PREP_DISPLAY_TRUE : this.EARLY_PREP_DISPLAY_FALSE;
  }

  public allDayViewText(allDayViewChangedState: boolean): string {
    return allDayViewChangedState ? this.ALL_DAY_VIEW_DISPLAY_TRUE : this.ALL_DAY_VIEW_DISPLAY_FALSE;
  }
}
